import { Check } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  needsReviewCount,
  visitListStatus,
} from "@/pages/Visits/redesign/visit-list-model";
import type { VisitSummary } from "@/services/visits/read-model";

export type VisitGlyph =
  | "done"
  | "review"
  | "writing"
  | "error"
  | "draft"
  | "none";

// One glyph per visit, as on iPad/iPhone and the v3.2 sidebar (.vglyph):
// a green check when ready, half amber while values still need review, a
// turning gradient while the note is being written, a red ring on error.
export const visitGlyph = (visit: VisitSummary): VisitGlyph => {
  const status = visitListStatus(visit.status);
  if (status === "error") return "error";
  if (status === "preChart") return "draft";
  // A dictated report is finished text, like a ready note.
  if (status === "report") return "done";
  if (status === "processing" || status === "generating") return "writing";
  if (status === "ready")
    return needsReviewCount(visit) > 0 ? "review" : "done";
  return "none";
};

export const VisitStatusIcon = ({
  glyph,
  className,
}: {
  glyph: VisitGlyph;
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const label = t(`v3.shell.status.${glyph}`);
  const base = cn(
    "relative inline-flex size-4 shrink-0 items-center justify-center rounded-full",
    className,
  );
  return (
    <span
      aria-label={label}
      className={cn(
        base,
        glyph === "done" && "bg-v3-ok text-white",
        glyph === "review" &&
          "bg-[linear-gradient(90deg,var(--v3-warn)_50%,transparent_50%)] shadow-[inset_0_0_0_1.5px_var(--v3-warn)]",
        glyph === "writing" &&
          "animate-spin bg-[conic-gradient(#00c2ff,#3d7bff,#8f7cff,#00c2ff)] [animation-duration:2s] motion-reduce:animate-none",
        glyph === "error" &&
          "shadow-[inset_0_0_0_1.5px_var(--v3-rec)] after:size-2 after:rounded-full after:bg-v3-rec",
        glyph === "draft" && "border-[1.5px] border-dashed border-v3-text-2",
        glyph === "none" && "shadow-[inset_0_0_0_1.5px_var(--v3-text-3)]",
      )}
      data-glyph={glyph}
      role="img"
    >
      {glyph === "done" && (
        <Check aria-hidden className="size-2.5" strokeWidth={3.5} />
      )}
    </span>
  );
};
